import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, getStudentId } from "@/lib/api";
import { playStreakCelebration } from "@/lib/sounds";
import { whenAudioIdle } from "@/lib/audioLock";
import Navbar from "@/components/Navbar";
import { ConceptVisuals } from "@/components/VisualWidget";
import Celebration from "@/components/Celebration";
import VoicePromptBanner from "@/components/VoicePromptBanner";
import { useVoicePrompt } from "@/hooks/use-voice-prompt";
import { randomCorrectPhrase, randomIncorrectPhrase, withRandomIntro } from "@/lib/questionPhrases";

const AUTO_ADVANCE_DELAY_MS = 2800;

export default function Assessment() {
  const { conceptId } = useParams();
  const navigate = useNavigate();
  const studentId = getStudentId();

  const [assessmentId, setAssessmentId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [concept, setConcept] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [checkResult, setCheckResult] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [celebrationTrigger, setCelebrationTrigger] = useState(0);

  const { blocked: voiceBlocked, retry: retryVoice } = useVoicePrompt(
    "Let's test what you've learned so far. Good luck!",
    `assessment-intro-${conceptId}`
  );
  const currentQ = questions[step];
  const questionVoiceText = useMemo(() => (currentQ ? withRandomIntro(currentQ.text) : ""), [currentQ?.id]);
  const { blocked: questionVoiceBlocked, retry: retryQuestionVoice } = useVoicePrompt(
    questionVoiceText,
    currentQ ? `assessment-q-${currentQ.id}` : null,
    { enabled: !!currentQ }
  );
  const feedbackVoiceText = useMemo(
    () => (checkResult && checkResult.correct !== null ? (checkResult.correct ? randomCorrectPhrase() : randomIncorrectPhrase()) : ""),
    [checkResult] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const { blocked: feedbackVoiceBlocked, retry: retryFeedbackVoice } = useVoicePrompt(
    feedbackVoiceText,
    checkResult && currentQ ? `assessment-feedback-${currentQ.id}` : null,
    { enabled: !!checkResult }
  );

  useEffect(() => {
    api.post("/assessment/start", { student_id: studentId, concept_id: conceptId, assessment_type: "practice" })
      .then(({ data }) => { setAssessmentId(data.assessment_id); setQuestions(data.questions); })
      .finally(() => setLoading(false));
    api.get(`/curriculum/concepts/${conceptId}`).then(({ data }) => setConcept(data));
  }, [conceptId, studentId]);

  const setAnswer = (val) => setAnswers((prev) => ({ ...prev, [questions[step].id]: val }));

  const next = async () => {
    setCheckResult(null);
    if (step + 1 < questions.length) {
      setStep(step + 1);
    } else {
      const { data } = await api.post("/assessment/submit", { assessment_id: assessmentId, answers });
      setResult(data);
      if (data.score >= 80) {
        setCelebrationTrigger((t) => t + 1);
        playStreakCelebration();
      }
    }
  };

  const checkAnswer = async (givenAnswer) => {
    if (checkResult || !currentQ) return;
    const { data } = await api.post("/assessment/check-answer", { question_id: currentQ.id, answer: givenAnswer });
    setCheckResult(data);
  };

  const selectOption = (opt) => {
    if (checkResult) return;
    setAnswer(opt);
    checkAnswer(opt);
  };

  useEffect(() => {
    if (!checkResult) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      await whenAudioIdle();
      if (!cancelled) next();
    }, AUTO_ADVANCE_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkResult]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Preparing assessment...</div>;

  if (result) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar student={null} />
        <Celebration trigger={celebrationTrigger} message="Great job! 🎉" />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="assessment-result-screen">
          <h1 className="text-2xl font-heading font-extrabold mb-2">Assessment Complete!</h1>
          <p className="text-lg text-primary font-bold mb-6" data-testid="assessment-score">Score: {result.score}% ({result.correct_count}/{result.total})</p>
          <div className="space-y-3 mb-8">
            {result.results.map((r, i) => (
              <div key={r.question_id} className="bg-card border border-border rounded-xl p-4" data-testid={`assessment-result-item-${i}`}>
                <div className={`flex items-center gap-2 font-semibold text-sm mb-1 ${r.correct ? "text-success" : "text-destructive"}`}>
                  {r.correct ? <CheckCircle2 size={15} /> : <XCircle size={15} />} Question {i + 1}
                </div>
                <p className="text-xs text-muted-foreground">Your answer: {String(r.answer_given)} · Correct: {r.correct_answer}</p>
                <p className="text-xs text-muted-foreground mt-1">{r.explanation}</p>
              </div>
            ))}
          </div>
          <Button data-testid="assessment-done-button" onClick={() => navigate("/")} className="rounded-xl px-6">Back to Home</Button>
        </div>
      </div>
    );
  }

  const q = currentQ;
  return (
    <div className="min-h-screen bg-background">
      <Navbar student={null} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/")} data-testid="assessment-back-button" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft size={15} /> Back to Home
        </button>
        <VoicePromptBanner
          blocked={voiceBlocked || questionVoiceBlocked || feedbackVoiceBlocked}
          onRetry={() => { retryVoice(); retryQuestionVoice(); retryFeedbackVoice(); }}
          testId="assessment-voice-unlock-banner"
        />
        <div className="flex gap-1.5 mb-6">
          {questions.map((_, i) => (<div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`} />))}
        </div>
        <p className="text-xs text-muted-foreground mb-2">Question {step + 1} of {questions.length}</p>
        <ConceptVisuals concept={concept} />
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="assessment-question-card">
          <p className="text-lg font-semibold mb-5" data-testid="assessment-question-text">{q.text}</p>
          {q.type === "mcq" ? (
            <div className="space-y-2">
              {q.options?.map((opt) => {
                const isSelected = answers[q.id] === opt;
                const showCorrect = checkResult && isSelected && checkResult.correct;
                const showIncorrect = checkResult && isSelected && !checkResult.correct;
                return (
                  <button
                    key={opt}
                    data-testid={`assessment-option-${opt.replace(/\s+/g, '-')}`}
                    onClick={() => selectOption(opt)}
                    disabled={!!checkResult}
                    className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-colors disabled:opacity-70 ${
                      showCorrect ? "border-success bg-success/10" : showIncorrect ? "border-destructive bg-destructive/10" :
                      isSelected ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          ) : (
            <Input data-testid="assessment-answer-input" value={answers[q.id] || ""} onChange={(e) => setAnswer(e.target.value)} disabled={!!checkResult} placeholder="Type your answer..." />
          )}

          {checkResult ? (
            <div className="mt-5 animate-float-in" data-testid="assessment-feedback">
              <div className={`flex items-center gap-2 font-semibold ${checkResult.correct ? "text-success" : "text-destructive"}`}>
                {checkResult.correct ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
                {checkResult.correct ? "Correct!" : "Not quite"}
              </div>
              {checkResult.explanation && <p className="text-sm text-muted-foreground mt-2">{checkResult.explanation}</p>}
              <Button variant="ghost" size="sm" data-testid="assessment-skip-pause-button" onClick={next} className="mt-3 text-xs">
                Continue now →
              </Button>
            </div>
          ) : (
            <div className="flex justify-end mt-6">
              {q.type !== "mcq" && (
                <Button data-testid="assessment-next-button" onClick={() => checkAnswer(answers[q.id])} disabled={!answers[q.id]} className="rounded-xl px-6">
                  Check Answer
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
